import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Project } from '@/types'
import { Loader2, AlertTriangle } from 'lucide-react'
import { SharedAnalyticsView } from '@/components/phase5-redesign/analytics/SharedAnalyticsView'

export const ShareView = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [showNameModal, setShowNameModal] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [guestName, setGuestName] = useState('')
  const [savingName, setSavingName] = useState(false)

  useEffect(() => {
    validateShareLink()
  }, [token])

  const validateShareLink = async () => {
    if (!token) {
      setError('Ongeldige share link')
      setLoading(false)
      return
    }

    try {
      // Check if share link exists and is valid
      const { data: shareData, error: shareError } = await supabase
        .from('project_shares')
        .select('*, projects(*)')
        .eq('share_token', token)
        .single()

      if (shareError || !shareData) {
        setError('Share link niet gevonden')
        setLoading(false)
        return
      }

      // Check if link has expired
      if (shareData.expires_at) {
        const expiryDate = new Date(shareData.expires_at)
        if (expiryDate < new Date()) {
          setError('Deze share link is verlopen')
          setLoading(false)
          return
        }
      }

      setProject(shareData.projects as Project)

      // Analytics share type — skip guest name, show read-only analytics
      if (shareData.share_type === 'analytics') {
        setShowAnalytics(true)
        setLoading(false)
        return
      }

      // Check if guest has already provided their name
      const storedGuestInfo = sessionStorage.getItem(`guest_${token}`)
      if (storedGuestInfo) {
        const guestInfo = JSON.parse(storedGuestInfo)
        setGuestName(guestInfo.name)
        // Proceed to project
        proceedToProject(shareData.projects.id)
      } else {
        // Show name modal
        setShowNameModal(true)
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Error validating share link:', err)
      setError('An error occurred while loading the link')
      setLoading(false)
    }
  }

  const handleSaveName = async () => {
    if (!guestName.trim()) return

    try {
      setSavingName(true)

      // Store guest info in session storage
      const guestInfo = {
        name: guestName.trim(),
        token,
        timestamp: new Date().toISOString(),
      }
      sessionStorage.setItem(`guest_${token}`, JSON.stringify(guestInfo))

      // Store in localStorage as well for persistence
      localStorage.setItem(`guest_${token}`, JSON.stringify(guestInfo))

      // Log the access (optional - for analytics)
      await supabase
        .from('project_share_access_log')
        .insert({
          share_token: token,
          guest_name: guestName.trim(),
          accessed_at: new Date().toISOString(),
        })
        .then(() => {}) // Ignore errors for logging

      // Proceed to project
      if (project) {
        proceedToProject(project.id)
      }
    } catch (err) {
      console.error('Error saving guest name:', err)
      // Continue anyway - name is stored in session
      if (project) {
        proceedToProject(project.id)
      }
    } finally {
      setSavingName(false)
    }
  }

  const proceedToProject = (projectId: string) => {
    const viewMap: Record<number, string> = { 1: 'build-discovery', 2: 'build-details', 3: 'build-spec' }
    const view = viewMap[project?.current_step || 1] || 'build-discovery'
    navigate(`/project/${projectId}?view=${view}&share=${token}`)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-framed-yellow-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-black mb-2">Link not available</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-framed-yellow-600 text-white rounded-lg font-medium hover:bg-framed-yellow-700 transition-colors"
          >
            Ga naar login
          </a>
        </div>
      </div>
    )
  }

  if (showAnalytics && project) {
    return <SharedAnalyticsView projectId={project.id} projectName={project.name} />
  }

  if (showNameModal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-framed-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-framed-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-black mb-2">Welcome to Framed</h2>
            <p className="text-gray-600">
              You've been invited to view the project <strong>{project?.name}</strong>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="guestName" className="block text-sm font-medium text-gray-700 mb-2">
                Your name
              </label>
              <input
                id="guestName"
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && guestName.trim()) {
                    handleSaveName()
                  }
                }}
                className="w-full h-12 px-4 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-framed-yellow-600 focus:border-transparent"
                placeholder="Enter your name..."
                disabled={savingName}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                We use your name to show who provides feedback
              </p>
            </div>

            <button
              onClick={handleSaveName}
              disabled={!guestName.trim() || savingName}
              className="w-full h-12 px-6 bg-framed-yellow-600 text-white rounded-lg font-medium hover:bg-framed-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {savingName ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Continue to project'
              )}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              By continuing, you accept that your feedback and changes will be visible to the project team
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}
